// ./src/components/features/Categories/CategoriesList/CategoriesList.tsx
//
// Categories list — table on desktop (>= md), cards on tablet/mobile. Tap-safe
// (explicit Edit/Delete only), rights-gated per row (system defaults + space-
// scoped rows are read-only and not selectable), and selection-ready. Mirrors the
// Highlights list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ColorSwatch,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { Category, CATEGORY_GROUP_COLOR, categoryIconName } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Props
// ============================================

export interface CategoriesListProps {
  data?: Category[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditCategory?: (category: Category) => void;
  onDeleteCategory?: (category: Category) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddCategory?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (category: Category): boolean => category.status === STATUSES.DISABLED;

/** A row is editable when the caller has any right on it (BE sets [] for read-only rows). */
const isEditableRow = (category: Category): boolean =>
  recordHasRight(category, "edit") || recordHasRight(category, "remove");

const GroupBadge: React.FC<{ group?: string | null }> = ({ group }) => {
  const { t } = useTranslation(["categories"]);

  if (!group) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color={CATEGORY_GROUP_COLOR[group] ?? "gray"}>
      {t(`categories:groups.${group}`, { defaultValue: group.replace(/_/g, " ") })}
    </Badge>
  );
};

const StatusBadge: React.FC<{ category: Category }> = ({ category }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(category)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

// A read-only row's origin: a system default (no space) or a space-scoped one.
const ScopeBadge: React.FC<{ category: Category; editable: boolean }> = ({ category, editable }) => {
  const { t } = useTranslation(["categories"]);

  if (editable) {
    return null;
  }

  const isSpace = !!category.spaceId;

  return (
    <Tooltip
      label={
        isSpace
          ? t("categories:scope.spaceHint", { defaultValue: "Managed in a space — read-only here" })
          : t("categories:scope.defaultHint", { defaultValue: "Built-in default — read-only" })
      }
      withArrow
    >
      <Badge size="xs" variant="outline" color="gray" leftSection={<AppIcon iconName="Lock" size={10} />}>
        {isSpace
          ? t("categories:scope.space", { defaultValue: "Space" })
          : t("categories:scope.system", { defaultValue: "System" })}
      </Badge>
    </Tooltip>
  );
};

const CategoryIcon: React.FC<{ category: Category; size?: number }> = ({ category, size = 34 }) => {
  const color = category.color || null;

  return (
    <ThemeIcon
      variant="light"
      color={CATEGORY_GROUP_COLOR[category.categoryGroup ?? "general"] ?? "gray"}
      radius="xl"
      size={size}
      style={color ? { color, backgroundColor: `${color}22` } : undefined}
    >
      <AppIcon iconName={categoryIconName(category)} size={Math.round(size * 0.55)} />
    </ThemeIcon>
  );
};

const RowActions: React.FC<{
  category: Category;
  onEdit?: (c: Category) => void;
  onDelete?: (c: Category) => void;
  isDeleting: boolean;
}> = ({ category, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(category, "edit");
  const showDelete = !!onDelete && recordHasRight(category, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(category)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(category)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  category: Category;
  name: string;
  editable: boolean;
  disabled: boolean;
  onEdit?: (c: Category) => void;
  onDelete?: (c: Category) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const CategoryTableRow: React.FC<RowProps> = ({
  category,
  name,
  editable,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => category.id && onToggleSelect?.(category.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <CategoryIcon category={category} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              {category.color && <ColorSwatch color={category.color} size={14} />}
              <ScopeBadge category={category} editable={editable} />
              <StatusBadge category={category} />
            </Group>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 200 }}>
        <GroupBadge group={category.categoryGroup} />
      </Table.Td>
      <Table.Td style={{ width: 180 }}>
        <Text size="md" c="dimmed">
          {formatSmartDateTime(category.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions category={category} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const CategoryTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Skeleton height={18} width={90} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const CategoryCardComponent: React.FC<CardProps> = ({
  category,
  name,
  editable,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => category.id && onToggleSelect?.(category.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(category, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(category),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(category, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(category),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, category, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <CategoryIcon category={category} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            {category.color && <ColorSwatch color={category.color} size={14} />}
            <StatusBadge category={category} />
          </Group>
          <Group gap={6} wrap="wrap" mt={4}>
            <GroupBadge group={category.categoryGroup} />
            <ScopeBadge category={category} editable={editable} />
          </Group>
        </Box>
        {showActions && (
          <RowActions category={category} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const CategoryCard = React.memo(CategoryCardComponent);

const CategoryCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={2} sizes={['md', 'xs']} gap={1} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const CategoriesListComponent: React.FC<CategoriesListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditCategory,
  onDeleteCategory,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddCategory,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["categories", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditCategory, onDeleteCategory);

  const categoriesContext = useOptionalCategoriesContext();

  const categories = dataProp ?? categoriesContext?.categories ?? [];
  const loading = loadingProp ?? categoriesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? categoriesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? categoriesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? categoriesContext?.loadMore;
  const isRemoving = isRemovingProp ?? categoriesContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: categories.length });

  const handleEdit = useCallback(
    (category: Category) => {
      closeAllCards();
      onEditCategory?.(category);
    },
    [onEditCategory, closeAllCards]
  );

  const handleDelete = useCallback(
    (category: Category) => {
      closeAllCards();
      onDeleteCategory?.(category);
    },
    [onDeleteCategory, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = categoriesContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && categories.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("categories:emptyState.noMatch", { defaultValue: "No categories match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("categories:emptyState.title", { defaultValue: "No categories yet" })}
          description={t("categories:emptyState.description", {
            defaultValue: "Create a reusable category to organize your events.",
          })}
          iconName="Tag"
          actionLabel={t("categories:emptyState.actionLabel", { defaultValue: "Add category" })}
          onAction={onAddCategory}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (category: Category): boolean =>
    isSelectable &&
    ((!!onEditCategory && recordHasRight(category, "edit")) ||
      (!!onDeleteCategory && recordHasRight(category, "remove")));

  const rowProps = (category: Category): RowProps => ({
    category,
    name: category.name || "—",
    editable: isEditableRow(category),
    disabled: isDisabled(category),
    onEdit: onEditCategory ? handleEdit : undefined,
    onDelete: onDeleteCategory ? handleDelete : undefined,
    highlighted: category.id === highlightedId,
    isDeleting: isRemoving(category.id),
    language,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(category),
    selected: selection?.isSelected(category.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = categories
    .filter((c) => rowIsSelectable(c))
    .map((c) => c.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("categories:list.columns.name", { defaultValue: "Category" })}</Table.Th>
                <Table.Th style={{ width: 200 }}>
                  {t("categories:list.columns.group", { defaultValue: "Group" })}
                </Table.Th>
                <Table.Th style={{ width: 180 }}>
                  {t("categories:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <CategoryTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : categories.map((category) => (
                    <CategoryTableRow key={category.id} {...rowProps(category)} />
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <CategoryCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {categories.map((category) => (
            <motion.div
              key={category.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <CategoryCard
                {...rowProps(category)}
                forceClose={shouldForceClose(category.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(category.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(category.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const CategoriesList = React.memo(CategoriesListComponent);
